import { useState } from 'react'

import copy from '../../demo/fixtures/w5/copy.json'
import { formatUsd } from '../../demo/format'
import { stationNow, useDemoStore } from '../../demo/store'
import { Button, Input, Modal } from '../../ui'
import { ProcessSteps } from '../analysis/ProcessSteps'
import { fillTemplate } from '../analysis/live'
import { useProcessRun } from '../analysis/useProcessRun'
import styles from './AutomationPage.module.css'

export type ProposalMode = 'approve' | 'modify' | 'reject'

export interface ProposalDialogState {
  mode: ProposalMode
  from: number
  to: number
  rule: string
  obj: string
}

const rejectReasons = ['本月预算不再追加', '想先看 7 天再说', '这个任务准备停投']

interface ProposalDecisionDialogProps extends ProposalDialogState {
  open: boolean
  onClose: () => void
  onDone: (message: string) => void
}

function nextActionAt(): string {
  const current = stationNow()
  const match = current.match(/^(\d{1,2}月\d{1,2}日)\s+(\d{2}):(\d{2})$/)
  if (!match) return current
  const total = Math.min(23 * 60 + 59, Number(match[2]) * 60 + Number(match[3]) + 1)
  return `${match[1]} ${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function ProposalDecisionDialog({ open, mode, from, to, rule, obj, onClose, onDone }: ProposalDecisionDialogProps) {
  const approve = useDemoStore((state) => state.approveW5Proposal)
  const reject = useDemoStore((state) => state.rejectW5Proposal)
  const [dailyMax, setDailyMax] = useState(String(to))
  const [reason, setReason] = useState(rejectReasons[0]!)
  const { visibleSteps, running, run } = useProcessRun()
  const parsed = Number(dailyMax)
  const invalid = !Number.isFinite(parsed) || parsed < from || parsed > to

  const confirm = async () => {
    if (mode !== 'reject' && invalid) return
    const chosen = mode === 'reject' ? to : mode === 'modify' ? parsed : to
    const pct = `+${((chosen / from - 1) * 100).toFixed(1)}%`
    const vars = {
      from: formatUsd(from, 0),
      to: formatUsd(chosen, 0),
      pct,
      maxPct: '20%',
      obj,
      rule,
      reason,
      at: nextActionAt(),
    }
    const templates = mode === 'reject' ? copy.process.proposalReject : copy.process.proposalApprove
    const completed = await run(templates.map((step) => ({ label: fillTemplate(step.label, vars), detail: fillTemplate(step.detail, vars) })), copy.process)
    if (!completed) return
    if (mode === 'reject') {
      reject(reason)
      onDone(`本次暂不调整 ${rule}：${reason}，${obj} 的日预算上限保持 ${formatUsd(from, 0)}`)
    } else {
      approve(chosen)
      onDone(`已批准 ${rule}：${obj} 的日预算上限 ${formatUsd(from, 0)} → ${formatUsd(chosen, 0)}（${pct}），明天 09:00 生效`)
    }
    onClose()
  }

  const title = mode === 'reject' ? '暂不调整' : mode === 'modify' ? '自定义金额' : '确认预算调整'
  return (
    <Modal open={open} title={title} hint={`${rule} · ${obj}`} onClose={running ? () => undefined : onClose} footer={<><Button variant="ghost" disabled={running} onClick={onClose}>取消</Button><Button disabled={running || (mode === 'modify' && invalid)} onClick={() => void confirm()}>{running ? '正在处理' : mode === 'reject' ? '确认暂不调整' : '确认并在明天生效'}</Button></>}>
      {mode === 'reject' ? (
        <div className={styles.decisionBody}>
          <p>选择这次不调整的原因</p>
          <div className={styles.reasonChoices}>{rejectReasons.map((value) => <Button key={value} aria-pressed={value === reason} className={`${styles.choiceButton} ${value === reason ? styles.choicePicked : ''}`} disabled={running} variant="ghost" onClick={() => setReason(value)}>{value}</Button>)}</div>
          <span>暂不调整后，触发记录会写明你的决定，任务预算保持 {formatUsd(from, 0)}。</span>
        </div>
      ) : (
        <div className={styles.decisionBody}>
          <p>把{obj}的日预算上限从 <b>{formatUsd(from, 0)}</b> 调到 <b>{mode === 'modify' ? dailyMax ? formatUsd(parsed, 0) : '—' : formatUsd(to, 0)}</b></p>
          {mode === 'modify' ? <><Input type="number" min={from} max={to} value={dailyMax} error={invalid} onChange={(event) => setDailyMax(event.target.value)} /><span className={invalid ? styles.inputError : ''}>单日预算变动 ≤ 20%，可填 {formatUsd(from, 0)}–{formatUsd(to, 0)}</span></> : null}
          <ul><li>明天 09:00 生效</li><li>确认后可从触发记录随时撤销</li><li>不改变任务状态与投放目标</li></ul>
        </div>
      )}
      {running ? <div className={styles.processSlot}><ProcessSteps steps={visibleSteps} /></div> : null}
    </Modal>
  )
}
